import {
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  makeStyles,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  Typography,
  withStyles,
} from "@material-ui/core";
import MuiTableCell from "@material-ui/core/TableCell";
import MuiTableRow from "@material-ui/core/TableRow";
import {
  AssessmentActivities,
  ExpertiseType,
  SubjectModel,
  TeachingActivity,
} from "../../../domain/SubjectModel";
import { RefQualificationLevelModel } from "../../../domain/RefQualificationLevelModel";
import React, { useCallback, useEffect, useState } from "react";
import { TextField } from "@ofqual/portal-components/components/TextField";
import {
  ApprovalStatus,
  ApprovalSubject,
  AssessmentEvaluation,
  Evaluation,
  IndustryEvaluation,
  TeachingEvaluation,
} from "../../../domain/ApprovalSubject";
import { RefQualificationTypeModel } from "../../../domain/RefQualificationTypeMode";
import { AssessmentApprovalCard } from "./AssessmentExperience/AssessmentApprovalCard";
import { IndustryApprovalCard } from "./IndustryExperience/IndustryApprovalCard";
import { TeachingApprovalCard } from "./TeachingExperience/TeachingApprovalCard";
import { useDispatch, useSelector } from "react-redux";
import {
  updateAssessmentExperienceApproval,
  updateIndustryExperienceApproval,
  updateTeachingExperienceApproval,
} from "../../../redux/actions";
import { evaluationSelector, itemSelector } from "../../../redux/selectors";
import { Skeleton } from "@material-ui/lab";
import { subjectDescriptionDisplayAssessment, subjectDescriptionDisplayIndustry, subjectDescriptionDisplayTeaching } from "../../../utils/evaluationNoteHelper";

import { authSelector } from "../../../redux/selectors";
import { UserRole } from "../../../domain/userRole";
import { ApplicationStatus } from "../../../domain/ExpertsApplyFormModel";


const useStyles = makeStyles(() => ({
  tableHead: {
    fontWeight: "bold",
    padding: 0,
  },
}));

const TableCell = withStyles({
  root: {
    borderBottom: "none",
    padding: "2px",
  },
})(MuiTableCell);

const TableRow = withStyles({
  root: {
    padding: 0,
  },
})(MuiTableRow);

interface IApprovalCardProps {
  subjects: SubjectModel[];
  evaluations: Evaluation | null;
  levels: RefQualificationLevelModel[];
  qualTypes: RefQualificationTypeModel[];
  assessmentEvalutation?: AssessmentEvaluation;
  industryEvaluation?: IndustryEvaluation;
  teachingEvaluation?: TeachingEvaluation;
  isAssessmentActivity?: boolean;
  isTeachingActivity?: boolean;
}

export const ApprovalCard: React.FC<IApprovalCardProps> = ({
  subjects,
  evaluations,
  levels,
  qualTypes,
  assessmentEvalutation,
  industryEvaluation,
  teachingEvaluation,
  isAssessmentActivity,
  isTeachingActivity,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  function getEvaluations(evaluations: Evaluation | null) {
    if (evaluations !== null) {
      return Object.values(evaluations)[0] as unknown as Evaluation;
    } else {
      return {
        assessmentEvaluation: {} as AssessmentEvaluation,
        teachingEvaluation: {} as TeachingEvaluation,
        industryEvaluation: {} as IndustryEvaluation,
        approvalSubjects: {} as ApprovalSubject[],
      } as Evaluation;
    }
  }

  const evaluation = React.useMemo<Evaluation>(() => {
    return getEvaluations(evaluations);
  }, [evaluations]) as unknown as Evaluation;

  const [approvalSubjects, setApprovalSubjects] = useState<ApprovalSubject[]>(
    evaluation.approvalSubjects as ApprovalSubject[]
  );
  const item = useSelector(itemSelector);
  const [rejectedReason, setRejectedReason] = useState<string>();
  const [check, setCheck] = useState<boolean>(false);
  const [checkError, setCheckError] = useState<boolean>(false);
  const { status } = useSelector(evaluationSelector);
  const [
    furtherEvidenceMakingAssessments,
    setFurtherEvidenceMakingAssessments,
  ] = useState<string>();
  const [
    furtherEvidenceMakingAssessmentsError,
    setFurtherEvidenceMakingAssessmentsError,
  ] = useState<boolean>(false);
  const [
    furtherEvidenceDesigningDevelopingAssessments,
    setFurtherEvidenceDesigningDevelopingAssessments,
  ] = useState<string>();
  const [
    furtherEvidenceDesigningDevelopingAssessmentsError,
    setFurtherEvidenceDesigningDevelopingAssessmentsError,
  ] = useState<boolean>(false);
  const [furtherEvidenceStandardSetting, setFurtherEvidenceStandardSetting] =
    useState<string>();
  const [
    furtherEvidenceStandardSettingError,
    setFurtherEvidenceStandardSettingError,
  ] = useState<boolean>(false);
  const [
    furtherEvidenceEvaluatingAssessments,
    setFurtherEvidenceEvaluatingAssessments,
  ] = useState<string>();
  const [
    furtherEvidenceEvaluatingAssessmentsError,
    setFurtherEvidenceEvaluatingAssessmentsError,
  ] = useState<boolean>(false);
  const [
    furtherEvidenceTeachingLecturingAssessment,
    setFurtherEvidenceTeachingLecturingAssessment,
  ] = useState<string>();
  const [
    furtherEvidenceTeachingLecturingAssessmentError,
    setFurtherEvidenceTeachingLecturingAssessmentError,
  ] = useState<boolean>(false);
  const [
    furtherEvidenceTrainingAssessment,
    setFurtherEvidenceTrainingAssessment,
  ] = useState<string>();
  const [
    furtherEvidenceTrainingAssessmentError,
    setFurtherEvidenceTrainingAssessmentError,
  ] = useState<boolean>(false);
  const [
    furtherEvidenceEducationManagementAssessment,
    setFurtherEvidenceEducationManagementAssessment,
  ] = useState<string>();
  const [
    furtherEvidenceEducationManagementAssessmentError,
    setFurtherEvidenceEducationManagementAssessmentError,
  ] = useState<boolean>(false);
  const [
    furtherEvidenceTeacherTrainingAssessment,
    setFurtherEvidenceTeacherTrainingAssessment,
  ] = useState<string>();
  const [
    furtherEvidenceTeacherTrainingAssessmentError,
    setFurtherEvidenceTeacherTrainingAssessmentError,
  ] = useState<boolean>(false);
  const [
    furtherEvidenceIndustryAssessment,
    setFurtherEvidenceIndustryAssessment,
  ] = useState<string>();
  const [
    furtherEvidenceIndustryAssessmentError,
    setFurtherEvidenceIndustryAssessmentError,
  ] = useState<boolean>(false);

  const { roles } = useSelector(authSelector);
  const readOnly =
    (!roles.includes(UserRole.OfqualExpertsEvaluator) && !roles.includes(UserRole.ITAdmin)) ||
    item.item?.applicationStatus !== ApplicationStatus["Ready for evaluation"];

  const submitAssessmentEvaluation = (
    data: ApprovalSubject[],
    subject: SubjectModel
  ) => {
    if (
      subject.selectedAssessmentActivities !== AssessmentActivities.None &&
      isAssessmentActivity
    ) {
      let error = false;
      if (
        checkForFurtherEvidenceRequiredMakingAssessment() &&
        !furtherEvidenceMakingAssessments
      ) {
        setFurtherEvidenceMakingAssessmentsError(true);
        error = true;
      } else {
        setFurtherEvidenceMakingAssessmentsError(false);
      }

      if (
        checkForFurtherEvidenceRequiredDesigningDevelopingAssessments() &&
        !furtherEvidenceDesigningDevelopingAssessments
      ) {
        setFurtherEvidenceDesigningDevelopingAssessmentsError(true);
        error = true;
      } else {
        setFurtherEvidenceDesigningDevelopingAssessmentsError(false);
      }

      if (
        checkForFurtherEvidenceRequiredStandardSetting() &&
        !furtherEvidenceStandardSetting
      ) {
        setFurtherEvidenceStandardSettingError(true);
        error = true;
      } else {
        setFurtherEvidenceStandardSettingError(false);
      }

      if (
        checkForFurtherEvidenceRequiredEvaluatingAssessments() &&
        !furtherEvidenceEvaluatingAssessments
      ) {
        setFurtherEvidenceEvaluatingAssessmentsError(true);
        error = true;
      } else {
        setFurtherEvidenceEvaluatingAssessmentsError(false);
      }

      if (!check && checkboxRequiredAssessment()) {
        setCheckError(true);
        error = true;
      } else {
        setCheckError(false);
      }

      if (
        rejectedReason ||
        furtherEvidenceMakingAssessments ||
        furtherEvidenceStandardSetting ||
        furtherEvidenceDesigningDevelopingAssessments ||
        furtherEvidenceEvaluatingAssessments
      ) {
        if (assessmentEvalutation === undefined) {
          assessmentEvalutation = {} as AssessmentEvaluation;
        }
        assessmentEvalutation.rejectedReason = rejectedReason;
        assessmentEvalutation.makingAssessmentFurtherEvidenceText =
          furtherEvidenceMakingAssessments;
        assessmentEvalutation.standardSettingFurtherEvidenceText =
          furtherEvidenceStandardSetting;
        assessmentEvalutation.designingDevelopingAssessmentFurtherEvidenceText =
          furtherEvidenceDesigningDevelopingAssessments;
        assessmentEvalutation.evaluatingAssessmentsFurtherEvidenceText =
          furtherEvidenceEvaluatingAssessments;
        assessmentEvalutation!.subjectDescriptions =
          subjectDescriptionDisplayAssessment(
            approvalSubjects,
            furtherEvidenceMakingAssessments,
            furtherEvidenceStandardSetting,
            furtherEvidenceDesigningDevelopingAssessments,
            furtherEvidenceEvaluatingAssessments,
            levels,
            subjects,
            qualTypes
          );
      }

      if (!error) {
        dispatch(
          updateAssessmentExperienceApproval(
            item.item!.id,
            data,
            assessmentEvalutation,
            check
          )
        );
      }
    }
  };

  const submitTeachingEvaluation = (
    data: ApprovalSubject[],
    subject: SubjectModel
  ) => {
    if (
      subject.teachingActivity !== TeachingActivity.None &&
      isTeachingActivity
    ) {
      let error = false;
      if (
        checkForFurtherEvidenceRequiredTeachingLecturingAssessment() &&
        !furtherEvidenceTeachingLecturingAssessment
      ) {
        setFurtherEvidenceTeachingLecturingAssessmentError(true);
        error = true;
      } else {
        setFurtherEvidenceTeachingLecturingAssessmentError(false);
      }
      if (
        checkForFurtherEvidenceRequiredTrainingAssessment() &&
        !furtherEvidenceTrainingAssessment
      ) {
        setFurtherEvidenceTrainingAssessmentError(true);
        error = true;
      } else {
        setFurtherEvidenceTrainingAssessmentError(false);
      }
      if (
        checkForFurtherEvidenceRequiredEducationManagementAssessment() &&
        !furtherEvidenceEducationManagementAssessment
      ) {
        setFurtherEvidenceEducationManagementAssessmentError(true);
        error = true;
      } else {
        setFurtherEvidenceEducationManagementAssessmentError(false);
      }
      if (
        checkForFurtherEvidenceRequiredTeacherTrainingAssessment() &&
        !furtherEvidenceTeacherTrainingAssessment
      ) {
        setFurtherEvidenceTeacherTrainingAssessmentError(true);
        error = true;
      } else {
        setFurtherEvidenceTeacherTrainingAssessmentError(false);
      }

      if (!check && checkboxRequiredTeaching()) {
        setCheckError(true);
        error = true;
      } else {
        setCheckError(false);
      }

      if (
        rejectedReason ||
        furtherEvidenceTeachingLecturingAssessment ||
        furtherEvidenceTrainingAssessment ||
        furtherEvidenceEducationManagementAssessment ||
        furtherEvidenceTeacherTrainingAssessment
      ) {
        if (teachingEvaluation === undefined) {
          teachingEvaluation = {} as TeachingEvaluation;
        }
        teachingEvaluation.rejectedReason = rejectedReason;
        teachingEvaluation.teachingLecturingAssessmentFurtherEvidenceText =
          furtherEvidenceTeachingLecturingAssessment;
        teachingEvaluation.trainingAssessmentFurtherEvidenceText =
          furtherEvidenceTrainingAssessment;
        teachingEvaluation.educationManagementAssessmentFurtherEvidenceText =
          furtherEvidenceEducationManagementAssessment;
        teachingEvaluation.teacherTrainingAssessmentFurtherEvidenceText =
          furtherEvidenceTeacherTrainingAssessment;
        teachingEvaluation.subjectDescriptions =
          subjectDescriptionDisplayTeaching(
            approvalSubjects,
            furtherEvidenceTeachingLecturingAssessment,
            furtherEvidenceTrainingAssessment,
            furtherEvidenceEducationManagementAssessment,
            furtherEvidenceTeacherTrainingAssessment,
            levels,
            subjects,
            qualTypes
          );
      }

      if (!error) {
        dispatch(
          updateTeachingExperienceApproval(
            item.item!.id,
            data,
            teachingEvaluation,
            check
          )
        );
      }
    }
  };

  const submitIndustryEvaluation = (data: ApprovalSubject[]) => {
    if (!isTeachingActivity && !isAssessmentActivity) {
      let error = false;
      if (
        checkForFurtherEvidenceRequiredIndustryAssessment() &&
        !furtherEvidenceIndustryAssessment
      ) {
        setFurtherEvidenceIndustryAssessmentError(true);
        error = true;
      } else {
        setFurtherEvidenceIndustryAssessmentError(false);
      }

      if (!check && checkboxRequiredIndustry()) {
        setCheckError(true);
        error = true;
      } else {
        setCheckError(false);
      }

      if (rejectedReason || furtherEvidenceIndustryAssessment) {
        if (industryEvaluation === undefined) {
          industryEvaluation = {} as IndustryEvaluation;
        }
        industryEvaluation.rejectedReason = rejectedReason;
        industryEvaluation.industryAssessmentFurtherEvidenceText =
          furtherEvidenceIndustryAssessment;
        industryEvaluation.subjectDescriptions =
          subjectDescriptionDisplayIndustry(
            approvalSubjects,
            furtherEvidenceIndustryAssessment,
            levels,
            subjects,
            qualTypes
          );
      }

      if (!error) {
        dispatch(
          updateIndustryExperienceApproval(
            item.item!.id,
            data,
            industryEvaluation,
            check
          )
        );
      }
    }
  };

  const handleSubmit = (data: ApprovalSubject[], subject: SubjectModel) => {
    submitAssessmentEvaluation(data, subject);

    submitTeachingEvaluation(data, subject);

    submitIndustryEvaluation(data);

    setCheck(false);
  };

  const checkForRejectedSubjects = useCallback(() => {
    return approvalSubjects.some((x) =>
      x.levels.some(
        (l) =>
          (l.evaluationResults!.makingAssessment?.approvalStatus ===
            ApprovalStatus.Rejected &&
            isAssessmentActivity) ||
          (l.evaluationResults!.designingDevelopingAssessments
            ?.approvalStatus === ApprovalStatus.Rejected &&
            isAssessmentActivity) ||
          (l.evaluationResults!.standardSetting?.approvalStatus ===
            ApprovalStatus.Rejected &&
            isAssessmentActivity) ||
          (l.evaluationResults!.evaluatingAssessments?.approvalStatus ===
            ApprovalStatus.Rejected &&
            isAssessmentActivity) ||
          (l.evaluationResults!.teachingLecturingAssessment?.approvalStatus ===
            ApprovalStatus.Rejected &&
            isTeachingActivity) ||
          (l.evaluationResults!.trainingAssessment?.approvalStatus ===
            ApprovalStatus.Rejected &&
            isTeachingActivity) ||
          (l.evaluationResults!.educationManagementAssessment
            ?.approvalStatus === ApprovalStatus.Rejected &&
            isTeachingActivity) ||
          (l.evaluationResults!.teacherTrainingAssessment?.approvalStatus ===
            ApprovalStatus.Rejected &&
            isTeachingActivity) ||
          (l.evaluationResults!.industryAssessment?.approvalStatus ===
            ApprovalStatus.Rejected &&
            !isTeachingActivity &&
            !isAssessmentActivity)
      )
    );
  }, [approvalSubjects, isAssessmentActivity, isTeachingActivity]);

  const checkForFurtherEvidenceRequiredMakingAssessment = useCallback(() => {
    return approvalSubjects.some((x) =>
      x.levels.some(
        (l) =>
          l.evaluationResults!.makingAssessment?.approvalStatus ===
          ApprovalStatus.FurtherEvidence && isAssessmentActivity
      )
    );
  }, [approvalSubjects, isAssessmentActivity]);

  const checkForFurtherEvidenceRequiredDesigningDevelopingAssessments =
    useCallback(() => {
      return approvalSubjects.some((x) =>
        x.levels.some(
          (l) =>
            l.evaluationResults!.designingDevelopingAssessments
              ?.approvalStatus === ApprovalStatus.FurtherEvidence &&
            isAssessmentActivity
        )
      );
    }, [approvalSubjects, isAssessmentActivity]);

  const checkForFurtherEvidenceRequiredStandardSetting = useCallback(() => {
    return approvalSubjects.some((x) =>
      x.levels.some(
        (l) =>
          l.evaluationResults!.standardSetting?.approvalStatus ===
          ApprovalStatus.FurtherEvidence && isAssessmentActivity
      )
    );
  }, [approvalSubjects, isAssessmentActivity]);

  const checkForFurtherEvidenceRequiredEvaluatingAssessments =
    useCallback(() => {
      return approvalSubjects.some((x) =>
        x.levels.some(
          (l) =>
            l.evaluationResults!.evaluatingAssessments?.approvalStatus ===
            ApprovalStatus.FurtherEvidence && isAssessmentActivity
        )
      );
    }, [approvalSubjects, isAssessmentActivity]);

  const checkForFurtherEvidenceRequiredTeachingLecturingAssessment =
    useCallback(() => {
      return approvalSubjects.some((x) =>
        x.levels.some(
          (l) =>
            l.evaluationResults!.teachingLecturingAssessment?.approvalStatus ===
            ApprovalStatus.FurtherEvidence && isTeachingActivity
        )
      );
    }, [approvalSubjects, isTeachingActivity]);

  const checkForFurtherEvidenceRequiredTrainingAssessment = useCallback(() => {
    return approvalSubjects.some((x) =>
      x.levels.some(
        (l) =>
          l.evaluationResults!.trainingAssessment?.approvalStatus ===
          ApprovalStatus.FurtherEvidence && isTeachingActivity
      )
    );
  }, [approvalSubjects, isTeachingActivity]);

  const checkForFurtherEvidenceRequiredEducationManagementAssessment =
    useCallback(() => {
      return approvalSubjects.some((x) =>
        x.levels.some(
          (l) =>
            l.evaluationResults!.educationManagementAssessment
              ?.approvalStatus === ApprovalStatus.FurtherEvidence &&
            isTeachingActivity
        )
      );
    }, [approvalSubjects, isTeachingActivity]);

  const checkForFurtherEvidenceRequiredTeacherTrainingAssessment =
    useCallback(() => {
      return approvalSubjects.some((x) =>
        x.levels.some(
          (l) =>
            l.evaluationResults!.teacherTrainingAssessment?.approvalStatus ===
            ApprovalStatus.FurtherEvidence && isTeachingActivity
        )
      );
    }, [approvalSubjects, isTeachingActivity]);

  const checkForFurtherEvidenceRequiredIndustryAssessment = useCallback(() => {
    return approvalSubjects.some((x) =>
      x.levels.some(
        (l) =>
          l.evaluationResults!.industryAssessment?.approvalStatus ===
          ApprovalStatus.FurtherEvidence &&
          !isTeachingActivity &&
          !isAssessmentActivity
      )
    );
  }, [approvalSubjects, isTeachingActivity, isAssessmentActivity]);

  const checkAssessmentComplete = useCallback(() => {
    let isSectionComplete = false;
    let totalQuestions = 0;

    for (let subject of subjects) {
      var countPerLevel = 0;

      if (
        subject.selectedAssessmentActivities &
        AssessmentActivities.MarkingAssessments
      ) {
        countPerLevel++;
      }
      if (
        subject.selectedAssessmentActivities &
        AssessmentActivities.StandardSettingAndAwardingQualifications
      ) {
        countPerLevel++;
      }
      if (
        subject.selectedAssessmentActivities &
        AssessmentActivities.DesigningAndDevelopingAssessments
      ) {
        countPerLevel++;
      }
      if (
        subject.selectedAssessmentActivities &
        AssessmentActivities.EvaluatingAssessments
      ) {
        countPerLevel++;
      }

      totalQuestions =
        totalQuestions + countPerLevel * subject.qualificationLevels.length;
    }

    for (let subject of subjects) {
      for (let level of subject.qualificationLevels) {
        for (let approvalSubject of approvalSubjects) {
          if (level.subjectId === approvalSubject.subjectId) {
            for (let approvalLevel of approvalSubject.levels) {
              if (approvalLevel.levelId === level.levelId) {
                if (
                  subject.selectedAssessmentActivities &
                  AssessmentActivities.MarkingAssessments
                ) {
                  if (
                    approvalLevel.evaluationResults?.makingAssessment
                      ?.approvalStatus !== null &&
                    approvalLevel.evaluationResults?.makingAssessment
                      ?.approvalStatus !== undefined
                  ) {
                    totalQuestions--;
                  }
                }
                if (
                  subject.selectedAssessmentActivities &
                  AssessmentActivities.StandardSettingAndAwardingQualifications
                ) {
                  if (
                    approvalLevel.evaluationResults?.standardSetting
                      ?.approvalStatus !== null &&
                    approvalLevel.evaluationResults?.standardSetting
                      ?.approvalStatus !== undefined
                  ) {
                    totalQuestions--;
                  }
                }
                if (
                  subject.selectedAssessmentActivities &
                  AssessmentActivities.DesigningAndDevelopingAssessments
                ) {
                  if (
                    approvalLevel.evaluationResults
                      ?.designingDevelopingAssessments?.approvalStatus !==
                    null &&
                    approvalLevel.evaluationResults
                      ?.designingDevelopingAssessments?.approvalStatus !==
                    undefined
                  ) {
                    totalQuestions--;
                  }
                }
                if (
                  subject.selectedAssessmentActivities &
                  AssessmentActivities.EvaluatingAssessments
                ) {
                  if (
                    approvalLevel.evaluationResults?.evaluatingAssessments
                      ?.approvalStatus !== null &&
                    approvalLevel.evaluationResults?.evaluatingAssessments
                      ?.approvalStatus !== undefined
                  ) {
                    totalQuestions--;
                  }
                }
              }
            }
          }
        }
      }
    }

    isSectionComplete = totalQuestions === 0 ? true : false;

    return isSectionComplete;
  }, [approvalSubjects, subjects]);

  const checkboxRequiredAssessment = useCallback(() => {
    let checkboxRequired = false;

    if (
      isAssessmentActivity &&
      checkAssessmentComplete() &&
      (checkForFurtherEvidenceRequiredMakingAssessment() ||
        checkForFurtherEvidenceRequiredDesigningDevelopingAssessments() ||
        checkForFurtherEvidenceRequiredStandardSetting() ||
        checkForFurtherEvidenceRequiredEvaluatingAssessments())
    ) {
      checkboxRequired = true;
    }

    return checkboxRequired;
  }, [
    checkAssessmentComplete,
    checkForFurtherEvidenceRequiredDesigningDevelopingAssessments,
    checkForFurtherEvidenceRequiredEvaluatingAssessments,
    checkForFurtherEvidenceRequiredMakingAssessment,
    checkForFurtherEvidenceRequiredStandardSetting,
    isAssessmentActivity,
  ]);

  const checkTeachingComplete = useCallback(() => {
    let isSectionComplete = false;
    let totalQuestions = 0;

    for (let subject of subjects) {
      var countPerLevel = 0;

      if (subject.teachingActivity & TeachingActivity.TeachingOrLecturing) {
        countPerLevel++;
      }
      if (subject.teachingActivity & TeachingActivity.Training) {
        countPerLevel++;
      }
      if (subject.teachingActivity & TeachingActivity.EducationalManagement) {
        countPerLevel++;
      }
      if (subject.teachingActivity & TeachingActivity.TeacherTraining) {
        countPerLevel++;
      }

      totalQuestions =
        totalQuestions + countPerLevel * subject.qualificationLevels.length;
    }

    for (let subject of subjects) {
      for (let level of subject.qualificationLevels) {
        for (let approvalSubject of approvalSubjects) {
          if (level.subjectId === approvalSubject.subjectId) {
            for (let approvalLevel of approvalSubject.levels) {
              if (approvalLevel.levelId === level.levelId) {
                if (
                  subject.teachingActivity &
                  TeachingActivity.TeachingOrLecturing
                ) {
                  if (
                    approvalLevel.evaluationResults?.teachingLecturingAssessment
                      ?.approvalStatus !== null &&
                    approvalLevel.evaluationResults?.teachingLecturingAssessment
                      ?.approvalStatus !== undefined
                  ) {
                    totalQuestions--;
                  }
                }
                if (subject.teachingActivity & TeachingActivity.Training) {
                  if (
                    approvalLevel.evaluationResults?.trainingAssessment
                      ?.approvalStatus !== null &&
                    approvalLevel.evaluationResults?.trainingAssessment
                      ?.approvalStatus !== undefined
                  ) {
                    totalQuestions--;
                  }
                }
                if (
                  subject.teachingActivity &
                  TeachingActivity.EducationalManagement
                ) {
                  if (
                    approvalLevel.evaluationResults
                      ?.educationManagementAssessment?.approvalStatus !==
                    null &&
                    approvalLevel.evaluationResults
                      ?.educationManagementAssessment?.approvalStatus !==
                    undefined
                  ) {
                    totalQuestions--;
                  }
                }
                if (
                  subject.teachingActivity & TeachingActivity.TeacherTraining
                ) {
                  if (
                    approvalLevel.evaluationResults?.teacherTrainingAssessment
                      ?.approvalStatus !== null &&
                    approvalLevel.evaluationResults?.teacherTrainingAssessment
                      ?.approvalStatus !== undefined
                  ) {
                    totalQuestions--;
                  }
                }
              }
            }
          }
        }
      }
    }

    isSectionComplete = totalQuestions === 0 ? true : false;

    return isSectionComplete;
  }, [approvalSubjects, subjects]);

  const checkboxRequiredTeaching = useCallback(() => {
    let checkboxRequired = false;
    if (
      isTeachingActivity &&
      checkTeachingComplete() && (
        checkForFurtherEvidenceRequiredTeachingLecturingAssessment() ||
        checkForFurtherEvidenceRequiredTrainingAssessment() ||
        checkForFurtherEvidenceRequiredEducationManagementAssessment() ||
        checkForFurtherEvidenceRequiredTeacherTrainingAssessment()
      )
    )
      checkboxRequired = true;
    return checkboxRequired;
  }, [checkForFurtherEvidenceRequiredEducationManagementAssessment, checkForFurtherEvidenceRequiredTeacherTrainingAssessment, checkForFurtherEvidenceRequiredTeachingLecturingAssessment, checkForFurtherEvidenceRequiredTrainingAssessment, checkTeachingComplete, isTeachingActivity])

  const checkIndustryComplete = useCallback(() => {
    let isSectionComplete = false;
    let totalQuestions = 0;

    for (let subject of subjects) {
      var countPerLevel = 0;

      if (subject.expertiseType & ExpertiseType.Industry) {
        countPerLevel++;
      }

      totalQuestions =
        totalQuestions + countPerLevel * subject.qualificationLevels.length;
    }

    for (let subject of subjects) {
      for (let level of subject.qualificationLevels) {
        for (let approvalSubject of approvalSubjects) {
          if (level.subjectId === approvalSubject.subjectId) {
            for (let approvalLevel of approvalSubject.levels) {
              if (approvalLevel.levelId === level.levelId) {
                if (subject.expertiseType & ExpertiseType.Industry) {
                  if (
                    approvalLevel.evaluationResults?.industryAssessment
                      ?.approvalStatus !== null &&
                    approvalLevel.evaluationResults?.industryAssessment
                      ?.approvalStatus !== undefined
                  ) {
                    totalQuestions--;
                  }
                }
              }
            }
          }
        }
      }
    }

    isSectionComplete = totalQuestions === 0 ? true : false;

    return isSectionComplete;
  }, [approvalSubjects, subjects]);

  const checkboxRequiredIndustry = useCallback(() => {
    let checkboxRequired = false;
    if (
      !isAssessmentActivity &&
      !isTeachingActivity &&
      checkIndustryComplete() &&
      checkForFurtherEvidenceRequiredIndustryAssessment()
    ) {
      checkboxRequired = true;
    }
    return checkboxRequired;
  }, [checkForFurtherEvidenceRequiredIndustryAssessment, checkIndustryComplete, isAssessmentActivity, isTeachingActivity]);

  useEffect(() => {
    if (evaluation.assessmentEvaluation !== undefined) {
      setFurtherEvidenceMakingAssessments(
        evaluation.assessmentEvaluation?.makingAssessmentFurtherEvidenceText
      );
      setFurtherEvidenceStandardSetting(
        evaluation.assessmentEvaluation?.standardSettingFurtherEvidenceText
      );
      setFurtherEvidenceDesigningDevelopingAssessments(
        evaluation.assessmentEvaluation
          ?.designingDevelopingAssessmentFurtherEvidenceText
      );
      setFurtherEvidenceEvaluatingAssessments(
        evaluation.assessmentEvaluation
          ?.evaluatingAssessmentsFurtherEvidenceText
      );
      if (isAssessmentActivity) {
        setRejectedReason(evaluation.assessmentEvaluation?.rejectedReason);
      }
    }
    if (evaluation.teachingEvaluation !== undefined) {
      setFurtherEvidenceTeachingLecturingAssessment(
        evaluation.teachingEvaluation
          ?.teachingLecturingAssessmentFurtherEvidenceText
      );
      setFurtherEvidenceTrainingAssessment(
        evaluation.teachingEvaluation?.trainingAssessmentFurtherEvidenceText
      );
      setFurtherEvidenceEducationManagementAssessment(
        evaluation.teachingEvaluation
          ?.educationManagementAssessmentFurtherEvidenceText
      );
      setFurtherEvidenceTeacherTrainingAssessment(
        evaluation.teachingEvaluation
          ?.teacherTrainingAssessmentFurtherEvidenceText
      );
      if (isTeachingActivity) {
        setRejectedReason(evaluation.teachingEvaluation?.rejectedReason);
      }
    }
    if (evaluation.industryEvaluation !== undefined) {
      setFurtherEvidenceIndustryAssessment(
        evaluation.industryEvaluation?.industryAssessmentFurtherEvidenceText
      );
      if (!isAssessmentActivity && !isTeachingActivity) {
        setRejectedReason(evaluation.industryEvaluation?.rejectedReason);
      }
    }
  }, [evaluation, isAssessmentActivity, isTeachingActivity]);

  return (
    <>
      {status !== "LOADING" ? (
        <>
          {subjects.map((s, subjectIndex) => {
            const matchingLevels = levels?.filter((q) =>
              s.qualificationLevels.some((s) => s.levelId === q.id)
            );

            const matchingQualType = qualTypes.find(
              (qt) => s.qualificationTypeId === qt.id
            );

            return (
              <React.Fragment key={subjectIndex}>
                <TableContainer>
                  {matchingLevels.length > 0 &&
                    matchingLevels.map((l, i) => (
                      <React.Fragment key={l.id}>
                        {i >= 0 && (
                          <>
                            <Grid item container spacing={1} xs={12}>
                              <Grid item xs={12} sm={3}>
                                <Typography
                                  variant="body1"
                                  data-testid="subject-title-key"
                                >
                                  <strong>Subject</strong>
                                </Typography>
                              </Grid>
                              <Grid item xs={12} sm={9}>
                                <Typography
                                  variant="body1"
                                  data-testid="subject-title-value"
                                >
                                  {s.name}
                                </Typography>
                              </Grid>

                              <Grid item xs={12} sm={3}>
                                <Typography
                                  variant="body1"
                                  data-testid="qualification-type-key"
                                >
                                  <strong>Qualification type</strong>
                                </Typography>
                              </Grid>
                              <Grid item xs={12} sm={9}>
                                <Typography
                                  variant="body1"
                                  data-testid="qualification-type-value"
                                >
                                  {matchingQualType?.code === "GceAsLevel" ? "GCE AS/A level" : matchingQualType?.description}
                                </Typography>
                              </Grid>

                              <Grid item xs={12} sm={3}>
                                <Typography
                                  variant="body1"
                                  data-testid="qualification-level-key"
                                >
                                  <strong>Qualification level</strong>
                                </Typography>
                              </Grid>
                              <Grid item xs={12} sm={9}>
                                <Typography
                                  variant="body1"
                                  data-testid="qualification-level-value"
                                >
                                  {matchingLevels[i].levelDescription}
                                </Typography>
                              </Grid>
                            </Grid>
                            <br />
                            <Table>
                              {(isAssessmentActivity || isTeachingActivity) && (
                                <TableHead>
                                  <TableRow>
                                    <TableCell className={classes.tableHead}>
                                      Activities
                                    </TableCell>
                                    <TableCell className={classes.tableHead}>
                                      Status
                                    </TableCell>
                                  </TableRow>
                                </TableHead>
                              )}
                              <TableBody>
                                {s.selectedAssessmentActivities !==
                                  AssessmentActivities.None &&
                                  isAssessmentActivity && (
                                    <AssessmentApprovalCard
                                      subject={s}
                                      matchingLevel={matchingLevels[i]}
                                      matchingQualType={matchingQualType}
                                      approvalSubjects={approvalSubjects}
                                      setApprovalSubjects={setApprovalSubjects}
                                      dataTest={`subject-${subjectIndex}-level-${i}`}
                                      readOnly={readOnly}
                                    />
                                  )}
                                {s.teachingActivity !== TeachingActivity.None &&
                                  isTeachingActivity && (
                                    <TeachingApprovalCard
                                      subject={s}
                                      matchingLevel={matchingLevels[i]}
                                      matchingQualType={matchingQualType}
                                      approvalSubjects={approvalSubjects}
                                      setApprovalSubjects={setApprovalSubjects}
                                      dataTest={`subject-${subjectIndex}-level-${i}`}
                                      readOnly={readOnly}
                                    />
                                  )}
                                {!!(s.expertiseType & ExpertiseType.Industry) &&
                                  !isAssessmentActivity &&
                                  !isTeachingActivity && (
                                    <IndustryApprovalCard
                                      subject={s}
                                      matchingLevel={matchingLevels[i]}
                                      matchingQualType={matchingQualType}
                                      approvalSubjects={approvalSubjects}
                                      setApprovalSubjects={setApprovalSubjects}
                                      dataTest={`subject-${subjectIndex}-level-${i}`}
                                      readOnly={readOnly}
                                    />
                                  )}
                              </TableBody>
                            </Table>
                          </>
                        )}
                      </React.Fragment>
                    ))}
                </TableContainer>
                {subjectIndex === subjects.length - 1 && (
                  <>
                    {checkForFurtherEvidenceRequiredMakingAssessment() && (
                      <Grid style={{ marginTop: "20px", marginBottom: "20px" }}>
                        <TextField
                          readOnly={readOnly}
                          multiline
                          value={furtherEvidenceMakingAssessments}
                          onChange={(e) =>
                            setFurtherEvidenceMakingAssessments(e.target.value)
                          }
                          rows={5}
                          label="Details of further evidence required for making assessment judgements"
                          helperText={
                            furtherEvidenceMakingAssessmentsError
                              ? "Details of further evidence for making assessment judgements is required"
                              : undefined
                          }
                          error={furtherEvidenceMakingAssessmentsError}
                          data-test="making-assessment-further-evidence"
                        />
                      </Grid>
                    )}
                    {checkForFurtherEvidenceRequiredDesigningDevelopingAssessments() && (
                      <Grid style={{ marginTop: "20px", marginBottom: "20px" }}>
                        <TextField
                          readOnly={readOnly}
                          multiline
                          value={furtherEvidenceDesigningDevelopingAssessments}
                          onChange={(e) =>
                            setFurtherEvidenceDesigningDevelopingAssessments(
                              e.target.value
                            )
                          }
                          rows={5}
                          label="Details of further evidence required for designing and developing assessments"
                          helperText={
                            furtherEvidenceDesigningDevelopingAssessmentsError
                              ? "Details of further evidence for designing and developing assessments is required"
                              : undefined
                          }
                          error={
                            furtherEvidenceDesigningDevelopingAssessmentsError
                          }
                          data-test="designing-developing-assessments-further-evidence"
                        />
                      </Grid>
                    )}
                    {checkForFurtherEvidenceRequiredStandardSetting() && (
                      <Grid style={{ marginTop: "20px", marginBottom: "20px" }}>
                        <TextField
                          readOnly={readOnly}
                          multiline
                          value={furtherEvidenceStandardSetting}
                          onChange={(e) =>
                            setFurtherEvidenceStandardSetting(e.target.value)
                          }
                          rows={5}
                          label="Details of further evidence required for standard setting and awarding qualifications"
                          helperText={
                            furtherEvidenceStandardSettingError
                              ? "Details of further evidence required for standard setting and awarding qualifications"
                              : undefined
                          }
                          error={furtherEvidenceStandardSettingError}
                          data-test="standard-setting-further-evidence"
                        />
                      </Grid>
                    )}
                    {checkForFurtherEvidenceRequiredEvaluatingAssessments() && (
                      <Grid style={{ marginTop: "20px", marginBottom: "20px" }}>
                        <TextField
                          readOnly={readOnly}
                          multiline
                          value={furtherEvidenceEvaluatingAssessments}
                          onChange={(e) =>
                            setFurtherEvidenceEvaluatingAssessments(
                              e.target.value
                            )
                          }
                          rows={5}
                          label="Details of further evidence required for evaluating assessments or assessment approaches"
                          helperText={
                            furtherEvidenceEvaluatingAssessmentsError
                              ? "Details of further evidence required for evaluating assessments or assessment approaches"
                              : undefined
                          }
                          error={furtherEvidenceEvaluatingAssessmentsError}
                          data-test="evaluating-assessments-further-evidence"
                        />
                      </Grid>
                    )}
                    {checkForFurtherEvidenceRequiredTeachingLecturingAssessment() && (
                      <Grid style={{ marginTop: "20px", marginBottom: "20px" }}>
                        <TextField
                          readOnly={readOnly}
                          multiline
                          value={furtherEvidenceTeachingLecturingAssessment}
                          onChange={(e) =>
                            setFurtherEvidenceTeachingLecturingAssessment(
                              e.target.value
                            )
                          }
                          rows={5}
                          label="Details of further evidence required for teaching or lecturing"
                          helperText={
                            furtherEvidenceTeachingLecturingAssessmentError
                              ? "Details of further evidence required for teaching or lecturing"
                              : undefined
                          }
                          error={
                            furtherEvidenceTeachingLecturingAssessmentError
                          }
                          data-test="teaching-lecturing-assessment-further-evidence"
                        />
                      </Grid>
                    )}
                    {checkForFurtherEvidenceRequiredTrainingAssessment() && (
                      <Grid style={{ marginTop: "20px", marginBottom: "20px" }}>
                        <TextField
                          readOnly={readOnly}
                          multiline
                          value={furtherEvidenceTrainingAssessment}
                          onChange={(e) =>
                            setFurtherEvidenceTrainingAssessment(e.target.value)
                          }
                          rows={5}
                          label="Details of further evidence required for training"
                          helperText={
                            furtherEvidenceTrainingAssessmentError
                              ? "Details of further evidence for training is required"
                              : undefined
                          }
                          error={furtherEvidenceTrainingAssessmentError}
                          data-test="training-assessment-further-evidence"
                        />
                      </Grid>
                    )}
                    {checkForFurtherEvidenceRequiredEducationManagementAssessment() && (
                      <Grid style={{ marginTop: "20px", marginBottom: "20px" }}>
                        <TextField
                          readOnly={readOnly}
                          multiline
                          value={furtherEvidenceEducationManagementAssessment}
                          onChange={(e) =>
                            setFurtherEvidenceEducationManagementAssessment(
                              e.target.value
                            )
                          }
                          rows={5}
                          label="Details of further evidence required for educational management"
                          helperText={
                            furtherEvidenceEducationManagementAssessmentError
                              ? "Details of further evidence for educational management is required"
                              : undefined
                          }
                          error={
                            furtherEvidenceEducationManagementAssessmentError
                          }
                          data-test="education-management-assessment-further-evidence"
                        />
                      </Grid>
                    )}
                    {checkForFurtherEvidenceRequiredTeacherTrainingAssessment() && (
                      <Grid style={{ marginTop: "20px", marginBottom: "20px" }}>
                        <TextField
                          readOnly={readOnly}
                          multiline
                          value={furtherEvidenceTeacherTrainingAssessment}
                          onChange={(e) =>
                            setFurtherEvidenceTeacherTrainingAssessment(
                              e.target.value
                            )
                          }
                          rows={5}
                          label="Details of further evidence required for teacher training"
                          helperText={
                            furtherEvidenceTeacherTrainingAssessmentError
                              ? "Details of further evidence for teacher training is required"
                              : undefined
                          }
                          error={furtherEvidenceTeacherTrainingAssessmentError}
                          data-test="teacher-training-assessment-further-evidence"
                        />
                      </Grid>
                    )}
                    {checkForFurtherEvidenceRequiredIndustryAssessment() && (
                      <Grid style={{ marginTop: "20px", marginBottom: "20px" }}>
                        <TextField
                          readOnly={readOnly}
                          multiline
                          value={furtherEvidenceIndustryAssessment}
                          onChange={(e) =>
                            setFurtherEvidenceIndustryAssessment(e.target.value)
                          }
                          rows={5}
                          label="Details of further evidence required for industry, occupational or professional"
                          helperText={
                            furtherEvidenceIndustryAssessmentError
                              ? "Details of further evidence for industry, occupational or professional is required"
                              : undefined
                          }
                          error={furtherEvidenceIndustryAssessmentError}
                          data-test="industry-assessment-further-evidence"
                        />
                      </Grid>
                    )}
                    {checkForRejectedSubjects() && (
                      <Grid style={{ marginTop: "20px", marginBottom: "20px" }}>
                        <TextField
                          readOnly={readOnly}
                          multiline
                          value={rejectedReason}
                          onChange={(e) => setRejectedReason(e.target.value)}
                          rows={5}
                          label="Add evaluator notes (internal use only)"
                          data-test="rejected-reason"
                        />
                      </Grid>
                    )}
                    {!readOnly &&
                      (checkboxRequiredAssessment() ||
                        checkboxRequiredTeaching() ||
                        checkboxRequiredIndustry()) && (
                        <FormControlLabel
                          control={
                            <Checkbox
                              data-test="citizen-response-checkbox"
                              onChange={(e) => setCheck(e.target.checked)}
                            />
                          }
                          label="I understand that this communication is directed at, and visible to, the citizen *"
                          labelPlacement="start"
                          style={{
                            color: checkError ? "red" : undefined,
                          }}
                        />
                      )}
                    {!readOnly && (
                      <Button
                        color="primary"
                        data-test="save-evaluation-button"
                        style={{ float: "right", marginTop: "5px" }}
                        onClick={() => {
                          handleSubmit(approvalSubjects, s);
                        }}
                      >
                        Save
                      </Button>
                    )}
                  </>
                )}
              </React.Fragment>
            );
          })}
        </>
      ) : (
        <>
          <Skeleton variant="text" height={60} />
          <br />
          <Skeleton variant="text" height={60} />
          <Skeleton variant="text" height={60} />
          <br />
          <Skeleton variant="rect" height={118} />
          <br />
          <Skeleton variant="text" height={60} />
          <br />
          <Skeleton variant="text" height={60} />
          <Skeleton variant="text" height={60} />
          <br />
          <Skeleton variant="rect" height={118} />
        </>
      )}
    </>
  );
};
